import { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'

import {
  buildConventionalScopeBody,
  ConventionalScope,
  conventionalScopeKey,
  coreApi,
  BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
  BlueprintFamilyMonthlyTrendsItem,
  BlueprintFamilyTrendsWindowDays,
} from '../core'
import { chunkIds, mapWithConcurrency, TRENDS_MAX_CONCURRENT_REQUESTS, TRENDS_MAX_IDS_PER_REQUEST } from './chunkedRequests'
import { DensityChange, densityChangeOf, densityScalarOf } from './trendsBatch'

// Density of one event category or event type: the last point of its own trends series.
// Each monthly value is the group's live-event days in the month divided by (segment games
// that ran the group that month × days in the month), the average number of its events
// running at once per adopting game (see `BlueprintFamilyMonthlyTrendsItem`).
// The figure is read from the group's own series, computed server-side over the whole
// group, not summed from its children's densities.
export type GroupDensityScalar = {
  id: string
  density: number | null
  // First- and last-month density of the selected period, from the same series; absent
  // when the request carried no window or the series lacks either month.
  densityChange?: DensityChange
  // Set when this id's chunk failed while others succeeded: `density` is then unknown,
  // not "no game in scope". A page should render it as unavailable (with a retry via
  // `refetch()`), not as a dash. The query only errors when every request failed.
  failed?: true
}

export type GetBlueprintFamilyGroupDensityScalarsQueryArgs = {
  eventGroupIds: string[]
  eventTypeIds: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

type ChunkResponse = { data?: unknown; error?: FetchBaseQueryError }
type FetchWithBaseQuery = (args: FetchArgs) => ChunkResponse | PromiseLike<ChunkResponse>

// The group-density queryFn body, kept free of RTK plumbing so it can be tested with a
// stubbed `fetchWithBQ`. Ids are chunked into ≤10-id requests, at most
// `TRENDS_MAX_CONCURRENT_REQUESTS` in flight at a time. Category and event-type ids
// never collide, so one list serves both tiers.
export async function fetchGroupDensityScalars(
  { eventGroupIds, eventTypeIds, windowDays, ...scope }: GetBlueprintFamilyGroupDensityScalarsQueryArgs,
  fetchWithBQ: FetchWithBaseQuery,
  now: Date = new Date()
): Promise<{ data: GroupDensityScalar[]; error?: undefined } | { error: FetchBaseQueryError; data?: undefined }> {
  const requests = [
    ...chunkIds(eventGroupIds, TRENDS_MAX_IDS_PER_REQUEST).map((chunk) => ({
      url: 'blueprint-families/trends/event-groups',
      body: { eventGroupIds: chunk },
      ids: chunk,
    })),
    ...chunkIds(eventTypeIds, TRENDS_MAX_IDS_PER_REQUEST).map((chunk) => ({
      url: 'blueprint-families/trends/event-types',
      body: { eventTypeIds: chunk },
      ids: chunk,
    })),
  ]
  if (requests.length === 0) return { data: [] }

  const responses = await mapWithConcurrency(requests, TRENDS_MAX_CONCURRENT_REQUESTS, (request) =>
    Promise.resolve(
      fetchWithBQ({
        url: request.url,
        method: 'POST',
        params: { ...(windowDays !== undefined ? { windowDays } : {}) },
        body: { ...request.body, ...buildConventionalScopeBody(scope) },
      })
    ).catch((error: unknown): ChunkResponse => ({ error: { status: 'FETCH_ERROR', error: String(error) } }))
  )

  // Surface an error only when every request failed, so one bad chunk can't blank
  // the whole column.
  const firstError = responses.find((res) => res.error)?.error
  if (firstError && responses.every((res) => res.error)) return { error: firstError }

  const resolved: GroupDensityScalar[] = []
  responses.forEach((res, index) => {
    if (res.error) {
      requests[index].ids.forEach((id) => resolved.push({ id, density: null, failed: true }))
      return
    }
    const items = (res.data as BlueprintFamilyMonthlyTrendsItem[] | undefined) ?? []
    const byId = new Map(items.map((item) => [item.id, item]))
    // An id with no tracked game in scope is absent from a successful response.
    for (const id of requests[index].ids) {
      const item = byId.get(id)
      const densityChange = item ? densityChangeOf(item, windowDays, now) : undefined
      resolved.push({ id, density: item ? densityScalarOf(item) : null, ...(densityChange && { densityChange }) })
    }
  })
  return { data: resolved }
}

const groupDensityScalarsApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    // Density scalars for every event category and event type row of the ranked list,
    // from `trends/event-groups` and `trends/event-types`. See `fetchGroupDensityScalars`.
    getBlueprintFamilyGroupDensityScalars: builder.query<GroupDensityScalar[], GetBlueprintFamilyGroupDensityScalarsQueryArgs>({
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      queryFn: (args, _queryApi, _extraOptions, fetchWithBQ) => fetchGroupDensityScalars(args, fetchWithBQ),
      providesTags: (_result, _err, { windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `group-density-scalars/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyGroupDensityScalarsQuery } = groupDensityScalarsApi
